import axios from 'axios';
import { rootDomainCookie, TRADEIN_ESTIMATE_COOKIE_NAME } from '@beef/shop-common';
import { captureException } from '@sentry/react';

import pushEvent, { EVENT_SELECT_TRADE_IN } from 'pushEvent';

import {
  UPDATE_OPTIONS_LIST,
  UPDATE_MAX_PRICE,
  SET_ACTIVE_OPTION,
  SET_WAIT_STATUS,
  SET_ERROR_STATUS,
} from './constants';
import {
  getTradeInActiveDeviceType,
  getTradeInActiveBrand,
  getTradeInActiveModel,
} from './selectors';

export const getDeviceTypes = () => (dispatch) => {
  dispatch({
    type: SET_WAIT_STATUS,
    payload: true,
  });
  axios({
    method: 'get',
    url: '/shop/trade-in/getdevicetypes/',
    responseType: 'json',
  })
    .then((response) => {
      const deviceTypes = ((response.data || {}).data || {}).deviceTypes || [];
      const deviceTypesOptions = deviceTypes.map((item) => ({
        label: item.title,
        value: item.productFamily,
      }));

      dispatch({
        type: UPDATE_OPTIONS_LIST,
        payload: {
          slug: 'deviceTypes',
          list: deviceTypesOptions,
        },
      });
      dispatch({
        type: SET_WAIT_STATUS,
        payload: false,
      });
    })
    .catch((error) => {
      captureException(error);
      dispatch({
        type: SET_WAIT_STATUS,
        payload: false,
      });
    });
};

export const selectDeviceOption = (slug, value) => (dispatch, getState) => {
  const state = getState();
  let gtmName = '';
  switch (slug) {
    case 'deviceTypes':
      getBrands(dispatch, value);
      gtmName = 'Устройство';
      break;
    case 'brands':
      getModels(dispatch, state, value);
      gtmName = 'Бренд';
      break;
    case 'models':
      getCapacity(dispatch, state, value);
      gtmName = 'Модель';
      break;
    case 'capacity':
      getMaxPrice(dispatch, state, value);
      gtmName = 'Память';
      break;
    default:
      // eslint-disable-next-line no-console
      console.info(`Unexpected slug "${slug}" in TradeIn page calculator`);
  }

  pushEvent(EVENT_SELECT_TRADE_IN, { name: gtmName });
};

export const showError = (dispatch) => {
  const actions = [
    { type: SET_ERROR_STATUS, payload: true },
    {
      type: SET_ACTIVE_OPTION,
      payload: {
        slug: 'deviceTypes',
        activeOption: null,
      },
    },
    {
      type: UPDATE_OPTIONS_LIST,
      payload: {
        slug: 'brands',
        list: [],
      },
    },
    {
      type: SET_ACTIVE_OPTION,
      payload: {
        slug: 'brands',
        activeOption: null,
      },
    },
    {
      type: UPDATE_OPTIONS_LIST,
      payload: {
        slug: 'models',
        list: [],
      },
    },
    {
      type: SET_ACTIVE_OPTION,
      payload: {
        slug: 'models',
        activeOption: null,
      },
    },
    {
      type: UPDATE_OPTIONS_LIST,
      payload: {
        slug: 'capacity',
        list: [],
      },
    },
    {
      type: SET_ACTIVE_OPTION,
      payload: {
        slug: 'capacity',
        activeOption: null,
      },
    },
    {
      type: SET_WAIT_STATUS,
      payload: false,
    },
  ];

  actions.forEach((action) => {
    dispatch(action);
  });
};

export const updateTotalValue = (total) => (dispatch) => {
  if (total === null) {
    rootDomainCookie.remove(TRADEIN_ESTIMATE_COOKIE_NAME);
  } else {
    rootDomainCookie.set(TRADEIN_ESTIMATE_COOKIE_NAME, {
      capacity: total.capacity,
      deviceBrand: total.brand,
      deviceModel: total.model,
      deviceType: total.deviceType,
      maxPrice: total.maxPrice,
    });
  }

  dispatch({
    type: UPDATE_MAX_PRICE,
    payload: total,
  });
};

export const getBrands = (dispatch, value) => {
  dispatch({
    type: SET_ERROR_STATUS,
    payload: false,
  });
  dispatch({
    type: SET_WAIT_STATUS,
    payload: true,
  });
  axios({
    method: 'get',
    url: '/shop/trade-in/getbrands/',
    responseType: 'json',
    params: {
      deviceType: value,
    },
  })
    .then((response) => {
      if ((response.data || {}).data) {
        const brands = ((response.data || {}).data || {}).brands || [];
        const brandsOptions = brands.map((item) => ({
          label: item,
          value: item,
        }));

        const actions = [
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'deviceTypes',
              activeOption: value,
            },
          },
          {
            type: UPDATE_OPTIONS_LIST,
            payload: {
              slug: 'brands',
              list: brandsOptions,
            },
          },
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'brands',
              activeOption: null,
            },
          },
          {
            type: UPDATE_OPTIONS_LIST,
            payload: {
              slug: 'models',
              list: [],
            },
          },
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'models',
              activeOption: null,
            },
          },
          {
            type: UPDATE_OPTIONS_LIST,
            payload: {
              slug: 'capacity',
              list: [],
            },
          },
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'capacity',
              activeOption: null,
            },
          },
          {
            type: SET_WAIT_STATUS,
            payload: false,
          },
        ];

        actions.forEach((action) => {
          dispatch(action);
        });
        dispatch(updateTotalValue(null));
      } else {
        showError(dispatch);
      }
    })
    .catch((error) => {
      captureException(error);
      showError(dispatch);
    });
};

export const getModels = (dispatch, state, value) => {
  const activeDeviceType = getTradeInActiveDeviceType(state);
  axios({
    method: 'get',
    url: '/shop/trade-in/getmodels/',
    responseType: 'json',
    params: {
      deviceType: activeDeviceType,
      brand: value,
    },
  })
    .then((response) => {
      if ((response.data || {}).data) {
        const models = ((response.data || {}).data || {}).models || [];
        const modelsOptions = models.map((item) => ({
          label: item,
          value: item,
        }));
        const actions = [
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'brands',
              activeOption: value,
            },
          },
          {
            type: UPDATE_OPTIONS_LIST,
            payload: {
              slug: 'models',
              list: modelsOptions,
            },
          },
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'models',
              activeOption: null,
            },
          },
          {
            type: UPDATE_OPTIONS_LIST,
            payload: {
              slug: 'capacity',
              list: [],
            },
          },
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'capacity',
              activeOption: null,
            },
          },
        ];
        actions.forEach((action) => {
          dispatch(action);
        });
        dispatch(updateTotalValue(null));
      } else {
        showError(dispatch);
      }
    })
    .catch((error) => {
      captureException(error);
      showError(dispatch);
    });
};

export const getCapacity = (dispatch, state, value) => {
  const activeDeviceType = getTradeInActiveDeviceType(state);
  const activeBrand = getTradeInActiveBrand(state);
  axios({
    method: 'get',
    url: '/shop/trade-in/getcapacity/',
    responseType: 'json',
    params: {
      deviceType: activeDeviceType,
      brand: activeBrand,
      model: value,
    },
  })
    .then((response) => {
      if ((response.data || {}).data) {
        const capacity = ((response.data || {}).data || {}).capacity || [];
        const capacityOptions = capacity.map((item) => ({
          label: item,
          value: item,
        }));
        const actions = [
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'models',
              activeOption: value,
            },
          },
          {
            type: UPDATE_OPTIONS_LIST,
            payload: {
              slug: 'capacity',
              list: capacityOptions,
            },
          },
          {
            type: SET_ACTIVE_OPTION,
            payload: {
              slug: 'capacity',
              activeOption: null,
            },
          },
        ];

        actions.forEach((action) => {
          dispatch(action);
        });
        dispatch(updateTotalValue(null));
      } else {
        showError(dispatch);
      }
    })
    .catch((error) => {
      captureException(error);
      showError(dispatch);
    });
};

export const getMaxPrice = (dispatch, state, value) => {
  const activeDeviceType = getTradeInActiveDeviceType(state);
  const activeBrand = getTradeInActiveBrand(state);
  const activeModel = getTradeInActiveModel(state);
  axios({
    method: 'get',
    url: '/shop/trade-in/getmaxprice/',
    responseType: 'json',
    params: {
      deviceType: activeDeviceType,
      brand: activeBrand,
      model: activeModel,
      capacity: value,
    },
  })
    .then((response) => {
      if (response?.data?.data) {
        dispatch({
          type: SET_ACTIVE_OPTION,
          payload: {
            slug: 'capacity',
            activeOption: value,
          },
        });

        dispatch(updateTotalValue(response.data.data));
      } else {
        showError(dispatch);
      }
    })
    .catch((error) => {
      captureException(error);
      showError(dispatch);
    });
};
